import { useState } from "react";
import { toWorker } from "../../util/toWorker.ts";
import type { ValueChangeCalculator } from "../../../worker/core/team/ValueChangeCalculator.ts";
type Evaluation = Awaited<
	ReturnType<ValueChangeCalculator["evaluateDetails"]>
> & { teamName: string };
const number = (n: number) => n.toFixed(2);
const factor = (n: number) =>
	`${n >= 1 ? "+" : ""}${((n - 1) * 100).toFixed(1)}%`;
export default function TradeEvaluation() {
	const [values, setValues] = useState<Evaluation[]>();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>();
	return (
		<details
			className="mt-2"
			onToggle={async (event) => {
				if (!event.currentTarget.open || values || loading) return;
				setLoading(true);
				setError(undefined);
				try {
					setValues(await toWorker("main", "getTradeEvaluation", undefined));
				} catch {
					setError(
						"Não foi possível calcular. Feche e abra para tentar novamente.",
					);
				} finally {
					setLoading(false);
				}
			}}
		>
			<summary>Como a IA avalia esta troca</summary>
			<p className="small text-body-secondary mt-2">
				Pontos de avaliação, não dinheiro nem probabilidade. Incluem qualidade,
				idade, estratégia e picks estimados. Controle e renovação são mostrados
				separadamente do talento. Encaixe usa ratings por posição e profundidade
				atual/futura. Pacotes grandes têm retorno marginal menor. O saldo não
				substitui as regras financeiras.
			</p>
			{loading ? <p role="status">Calculando…</p> : null}
			{error ? <p role="alert">{error}</p> : null}
			{values?.map((evaluation, i) => (
				<section key={evaluation.tid} className="mb-3 small">
					<strong>
						{evaluation.teamName} ·{" "}
						{evaluation.strategy === "contending"
							? "Disputando título"
							: "Reconstrução"}
					</strong>
					<div>
						Recebe {number(evaluation.received)} · Cede{" "}
						{number(evaluation.given)} · Saldo <b>{number(evaluation.net)}</b>
					</div>
					{i === 1 ? (
						<div
							className={evaluation.net > 0 ? "text-success" : "text-warning"}
						>
							{evaluation.net > 0
								? "Valor suficiente para a IA; sujeito às regras da troca."
								: "Valor insuficiente para a IA com este pacote."}
						</div>
					) : (
						<div className="text-body-secondary">
							Referência para o seu time; você decide.
						</div>
					)}
					<div className="mt-2 border rounded p-2">
       <div>Janela: {evaluation.competition.window} · C: {number(evaluation.competition.C)}</div>
       <div>Team OVR legal: {evaluation.before?.ovr ?? "—"} → {evaluation.after?.ovr ?? "—"} · impacto: {number(evaluation.immediateImpact)}</div>
       <div>Portfólio recebido — jogadores: {number(evaluation.receivedPortfolio.players)} · picks: {number(evaluation.receivedPortfolio.picks)}</div>
       <div>Redundância: {(100*evaluation.receivedPortfolio.redundancy).toFixed(1)}% · mercado: {number(evaluation.receivedPortfolio.total)}</div>
       <div>Utilidade recebida: {number(evaluation.received)} · custo de ceder: {number(evaluation.given)}</div>
       <div className="text-body-secondary">OVR considera apenas funções legais no elenco reorganizado. Sub-19 e excedentes sem função não criam impacto imediato.</div>
      </div>
      <div className="mt-2">
      {(["incoming", "outgoing"] as const).flatMap((direction) =>
							evaluation[direction].map((row) => (
								<details
									key={`${direction}-${row.type}-${row.id}`}
									className="border rounded p-2 mb-2"
								>
									<summary>
										{direction === "incoming" ? "Recebe" : "Cede"}: {row.name} ·{" "}
										<b>{number(row.value)} pts</b>
									</summary>
									<dl className="row gx-2 mb-1 mt-2">
										<dt className="col-7">
											{row.type === "player"
												? "Talento-base"
												: "Valor estimado da pick"}
										</dt>
										<dd className="col-5">{number(row.talent)}</dd>
										<dt className="col-7">Lesão / dificuldade</dt>
										<dd className="col-5">{number(row.strategyAdjustment)}</dd>
										<dt className="col-7">Ajuste salarial existente</dt>
										<dd className="col-5">{number(row.salaryAdjustment)}</dd>
										<dt className="col-7">Controle contratual</dt>
										<dd className="col-5">
											{(row.controlFactor * 100).toFixed(1)}%
										</dd>
										<dt className="col-7">Mood / resistência à venda</dt>
										<dd className="col-5">{factor(row.moodFactor)}</dd>
										<dt className="col-7">Afinidade com o time (traits)</dt>
										<dd className="col-5">{factor(row.traitFactor)}</dd>
										<dt className="col-7">Proteção de prospecto central</dt>
										<dd className="col-5">{factor(row.holdingFactor)}</dd>
										{row.prospectFactor !== undefined && row.prospectFactor !== 1 ? (
											<>
												<dt className="col-7">Desconto de prospecto (contender)</dt>
												<dd className="col-5">{factor(row.prospectFactor)}</dd>
											</>
										) : null}
										<dt className="col-7">Encaixe posicional</dt>
										<dd className="col-5">{factor(row.fitFactor)}</dd>
										<dt className="col-7">Participação no portfólio</dt>
										<dd className="col-5">{factor(row.packageFactor)}</dd>
										<dt className="col-7">Contribuição final</dt>
										<dd className="col-5">{number(row.value)}</dd>
									</dl>
									<div className="text-body-secondary">{row.reason}</div>
									{row.control ? (
										<div className="mt-2">
											<div>
												<b>Renewal outlook: {row.control.outlook}</b>
											</div>
											<div>
												Controle restante: {number(row.control.yearsRemaining)}{" "}
												temporadas equivalentes.
											</div>
											<div>
												Oportunidade:{" "}
												{row.control.opportunityProtected
													? "protegida antes dos 21; não gera pressão"
													: number(row.control.opportunity)}
												.
											</div>
											<div className="text-body-secondary">
												Interesse estimado para renovar com{" "}
												{evaluation.teamName}, não probabilidade garantida nem
												decisão já tomada. Rookie não conta como promessa de
												renovação.
											</div>
											<details className="mt-1">
												<summary>Fatores da perspectiva de renovação</summary>
												<ul className="ps-3 mb-1">
													{row.control.factors.map((item) => (
														<li key={item.label}>
															{item.label}: {number(item.value)}
														</li>
													))}
												</ul>
												<div>
													Índice de interesse:{" "}
													{(100 * row.control.renewalIndex).toFixed(1)}/100. Não
													é uma porcentagem de renovações.
												</div>
												<div>
													Salário de referência atual: $
													{(row.control.expectedSalary / 1000).toFixed(2)}M/ano;
													espaço estimado no orçamento na renovação: $
													{(row.control.availableSalary / 1000).toFixed(2)}
													M/ano.
												</div>
												<div className="text-body-secondary">
													Projeção com orçamento e compromissos atuais; futuras
													contratações e receitas podem mudar este cenário.
												</div>
											</details>
										</div>
									) : null}
								</details>
							)),
						)}
					</div>
				</section>
			))}
		</details>
	);
}
